import { useState, useCallback } from 'react'
import req from '../api/request'

export interface Coupon {
  id: string
  code: string
  discount: number
  discount_type: string
  is_active: boolean
  start_date: string
  end_date: string
  remaining: number
  email_matches: string[]
  is_team: boolean
  is_player: boolean
  applies_to_deposit: boolean
  // Pre-calculated values from coupon verification API
  subtotal?: number
  deposit_subtotal?: number
  discount_subtotal?: number
  deposit_discount_subtotal?: number
  discount_amount?: number
  deposit_discount_amount?: number
  is_deposit?: boolean
  is_installment?: boolean
  organization_id?: string | null
  registration_id?: string
}

interface CouponState {
  coupons: Coupon[]
  isLoading: boolean
  error: string | null
}

interface UseCouponsResult {
  couponState: CouponState
  createCoupon: (couponData: Omit<Coupon, 'id' | 'remaining'>) => Promise<void>
  updateCoupon: (couponId: string, couponData: Partial<Coupon>) => Promise<void>
  deleteCoupon: (couponId: string) => Promise<void>
  refreshCoupons: () => Promise<void>
}

interface UseCouponsParams {
  scheduleId?: string
  organizationId?: string
}

/**
 * Hook for managing coupons at either schedule or organization level
 *
 * @param params - Either a string (scheduleId for backward compatibility) or an object with scheduleId or organizationId
 *
 * Usage examples:
 *
 * For schedule-level coupons:
 * const coupons = useCoupons({ scheduleId: 'schedule-123' })
 * // or backward compatible:
 * const coupons = useCoupons('schedule-123')
 *
 * For organization-level coupons:
 * const coupons = useCoupons({ organizationId: 'org-456' })
 *
 * @returns Object containing coupon state and CRUD operations
 */
export const useCoupons = (
  params: UseCouponsParams | string
): UseCouponsResult => {
  // Handle backward compatibility - if a string is passed, treat it as scheduleId
  const { scheduleId, organizationId } =
    typeof params === 'string'
      ? { scheduleId: params, organizationId: undefined }
      : params

  if (!scheduleId && !organizationId) {
    throw new Error('Either Schedule ID or Organization ID is required')
  }

  if (scheduleId && organizationId) {
    throw new Error('Cannot provide both Schedule ID and Organization ID')
  }

  const [couponState, setCouponState] = useState<CouponState>({
    coupons: [],
    isLoading: false,
    error: null,
  })

  // Determine the base URL based on whether we're using schedule or organization
  const getBaseUrl = useCallback(() => {
    if (organizationId) {
      return `/organizations/${organizationId}/coupon`
    }
    return `/registration/${scheduleId}/admin/coupon`
  }, [scheduleId, organizationId])

  const refreshCoupons = useCallback(async () => {
    setCouponState((prev) => ({ ...prev, isLoading: true, error: null }))

    try {
      const { data } = await req(getBaseUrl())
      setCouponState({
        coupons: data,
        isLoading: false,
        error: null,
      })
    } catch (error) {
      setCouponState((prev) => ({
        ...prev,
        isLoading: false,
        error: error?.message || 'An error occurred',
      }))
    }
  }, [getBaseUrl])

  const createCoupon = useCallback(
    async (couponData: Omit<Coupon, 'id' | 'remaining'>) => {
      setCouponState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        await req(getBaseUrl(), {
          method: 'POST',
          body: JSON.stringify(couponData),
        })
        await refreshCoupons()
      } catch (error) {
        setCouponState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'An error occurred',
        }))
        throw error
      }
    },
    [getBaseUrl, refreshCoupons]
  )

  const updateCoupon = useCallback(
    async (couponId: string, couponData: Partial<Coupon>) => {
      setCouponState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        await req(`${getBaseUrl()}/${couponId}`, {
          method: 'PUT',
          body: JSON.stringify(couponData),
        })
        await refreshCoupons()
      } catch (error) {
        setCouponState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'An error occurred',
        }))
        throw error
      }
    },
    [getBaseUrl, refreshCoupons]
  )

  const deleteCoupon = useCallback(
    async (couponId: string) => {
      setCouponState((prev) => ({ ...prev, isLoading: true, error: null }))

      try {
        await req(`${getBaseUrl()}/${couponId}`, {
          method: 'DELETE',
        })
        await refreshCoupons()
      } catch (error) {
        setCouponState((prev) => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : 'An error occurred',
        }))
        throw error
      }
    },
    [getBaseUrl, refreshCoupons]
  )

  return {
    couponState,
    createCoupon,
    updateCoupon,
    deleteCoupon,
    refreshCoupons,
  }
}
